import * as React from 'react';
import type {V1ContextInput} from '../../schema/types';

type V1ProviderContext = Readonly<{
  accountId: string | null;
  id: string | null;
  apiKey: string | null;
  betaHeaders: NonNullable<V1ContextInput['betaHeaders']> | null;
  liveMode: NonNullable<V1ContextInput['liveMode']> | null;
}>;

export type V1Context = V1ProviderContext;

type V1ProviderProps = Partial<
  V1ProviderContext & {
    children: React.ReactNode;
  }
>;

const V1ContextContext = React.createContext<V1ProviderContext>({
  accountId: null,
  id: null,
  apiKey: null,
  betaHeaders: null,
  liveMode: null,
});

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export function useV1Context(): V1Context {
  const v1Context = React.useContext(V1ContextContext);
  const {accountId, id, apiKey, betaHeaders, liveMode} = v1Context;

  return React.useMemo(() => {
    return {accountId, id, apiKey, betaHeaders, liveMode};
  }, [accountId, apiKey, betaHeaders, id, liveMode]);
}

export function V1Provider({
  accountId,
  id,
  apiKey,
  betaHeaders,
  children,
  liveMode,
}: V1ProviderProps) {
  const parentValue = React.useContext(V1ContextContext);

  const value: V1ProviderContext = React.useMemo(() => {
    const resolvedAccountIdValue =
      accountId === undefined ? parentValue.accountId : accountId;

    const resolvedIdValue = id === undefined ? parentValue.id : id;

    const resolvedApiKeyValue =
      apiKey === undefined ? parentValue.apiKey : apiKey;

    const resolvedBetaHeaders =
      betaHeaders === undefined ? parentValue.betaHeaders : betaHeaders;

    const resolvedLiveModeValue =
      liveMode === undefined ? parentValue.liveMode : liveMode;

    return {
      accountId: resolvedAccountIdValue,
      id: resolvedIdValue,
      apiKey: resolvedApiKeyValue,
      betaHeaders: resolvedBetaHeaders,
      liveMode: resolvedLiveModeValue,
    };
  }, [parentValue, accountId, apiKey, betaHeaders, liveMode, id]);

  return (
    <V1ContextContext.Provider value={value}>
      {children}
    </V1ContextContext.Provider>
  );
}
